/**
 * Admin / lead backfill API для трекинга времени (task 17 §3.11).
 *
 * Параллельный flow worker-/api/field/timesheet/*, но:
 *  - admin/лид авторизуется session-cookie'й (не PIN рабочего);
 *  - все CRUD-роуты принимают `user_id` + `date` (за кого и за какой день);
 *  - sign — БЕЗ pin рабочего (см. AdminSignPayload).
 *
 * `/admin/timesheet/work-types` — read-only справочник под ActsOnUnit
 * (отдельно от admin-only CRUD `/admin/work-types`), нужен лиду, чтобы
 * рендерить ленту work_types в форме backfill.
 */
import { api } from "./client";
import type { components } from "./types";

export type AdminTimesheetWorker = components["schemas"]["AdminTimesheetWorker"];
export type AdminTimesheetSnapshot = components["schemas"]["AdminTimesheetSnapshot"];
export type AdminSignPayload = components["schemas"]["AdminSignPayload"];
export type TimesheetEntry = components["schemas"]["TimesheetEntry"];
export type TimesheetEntryPayload = components["schemas"]["TimesheetEntryPayload"];
/** Тот же schema, что `/field` `my_work_types` — форма, которую ждёт единый
 *  редактор записи (`EntryEditorCore`, задача 77 §3.1/§4.1). */
export type WorkType = components["schemas"]["WorkType"];
export type SignResponse = components["schemas"]["SignResponse"];
export type Project = components["schemas"]["Project"];

export function listAdminTimesheetWorkers(): Promise<AdminTimesheetWorker[]> {
  return api.get<AdminTimesheetWorker[]>("/admin/timesheet/users");
}

export function getAdminTimesheet(userId: number, date: string): Promise<AdminTimesheetSnapshot> {
  return api.get<AdminTimesheetSnapshot>(
    `/admin/timesheet?user_id=${userId}&date=${encodeURIComponent(date)}`,
  );
}

export function listBackfillWorkTypes(): Promise<WorkType[]> {
  return api.get<WorkType[]>("/admin/timesheet/work-types");
}

export function listUserBackfillWorkTypes(userId: number): Promise<WorkType[]> {
  return api.get<WorkType[]>(`/admin/timesheet/users/${userId}/work-types`);
}

/** «Горячие» проекты сотрудника (последний месяц) — чипы-подсказки в шаге
 *  «проект» при вводе за сотрудника (§4.10). Тот же источник, что у self. */
export function listUserRecentProjects(userId: number): Promise<Project[]> {
  return api.get<Project[]>(`/admin/timesheet/users/${userId}/recent-projects`);
}

function qs(userId: number, date: string): string {
  return `?user_id=${userId}&date=${encodeURIComponent(date)}`;
}

export function createAdminEntry(
  userId: number,
  date: string,
  payload: TimesheetEntryPayload,
): Promise<TimesheetEntry> {
  return api.post<TimesheetEntry>(`/admin/timesheet/entries${qs(userId, date)}`, payload);
}

export function replaceAdminEntry(
  userId: number,
  date: string,
  entryId: number,
  payload: TimesheetEntryPayload,
): Promise<TimesheetEntry> {
  return api.put<TimesheetEntry>(`/admin/timesheet/entries/${entryId}${qs(userId, date)}`, payload);
}

export function deleteAdminEntry(userId: number, date: string, entryId: number): Promise<void> {
  return api.delete<void>(`/admin/timesheet/entries/${entryId}${qs(userId, date)}`);
}

export function signAdminTimesheet(
  userId: number,
  date: string,
  payload: AdminSignPayload,
): Promise<SignResponse> {
  return api.post<SignResponse>(`/admin/timesheet/sign${qs(userId, date)}`, payload);
}
